import type { Dispatch, ListenerEffectAPI } from '@reduxjs/toolkit';

import { setSentryUser, unsetSentryUser } from '/common/Sentry';
import { isTokenExpiringSoon, isValidToken } from '/helpers';
import { systemRehydrate } from '/store/systemActions';
import { startAppListening } from '/store/listener';
import { snackbarService } from '/common/snackbarService';
import { authService, permsService } from '/api/domains';
import { setAuthorization } from '/api/core';

import { initialState } from './state';
import type { IAuthState } from './types';
import { rehydrate, updateServicesAuthorizationStatus, getPermsByCompany } from './slice';
import { selectUser, isAdmin } from './selectors';
import { isTokenRefresherActive, setTokenRefresherTimer, stopTokenRefresher } from './tokenTimer';

const TOKEN_CHECK_INTERVAL_MS = 5 * 60 * 1000;

async function refreshToken(serializedAuth: IAuthState) {
  const onError = () => {
    stopTokenRefresher();
    return initialState;
  };
  const data = await authService.refreshToken(serializedAuth.tokens.refresh);
  return data.bifold(
    () =>
      authService.invalidateToken().then((tokensData) =>
        tokensData.bifold(
          (tokens) => ({
            ...serializedAuth,
            tokens: {
              access: tokens.access,
              refresh: tokens.refresh,
            },
          }),
          onError
        )
      ),
    onError
  );
}

function parseAuth$(auth: string, fallback: IAuthState): Promise<IAuthState> {
  try {
    const serializedAuth = JSON.parse(auth) as IAuthState | undefined;

    if (!serializedAuth) {
      return Promise.resolve(fallback);
    }

    if (isValidToken(serializedAuth.tokens.access) && !isTokenExpiringSoon(serializedAuth.tokens.access)) {
      return Promise.resolve(serializedAuth);
    }

    if (serializedAuth.tokens.refresh) {
      return refreshToken(serializedAuth);
    }

    const { googleRedirectURL, rememberMe }: IAuthState = serializedAuth;

    return Promise.resolve({
      ...fallback,
      ...(googleRedirectURL ? { googleRedirectURL } : null),
      ...(!rememberMe ? { rememberMe } : null),
    });
  } catch (e) {
    console.error(`Can't read auth state from localStorage`, e);

    return Promise.resolve(fallback);
  }
}

const getPersistedAuth = () => {
  const persistedAuth: string | null = localStorage.getItem('auth');
  const persistedAuthSession: string | null = sessionStorage.getItem('auth');

  const defaultState = initialState;

  return { persistedAuth, persistedAuthSession, defaultState };
};

const fetchIniAuthState = (): Promise<IAuthState> => {
  const { persistedAuth, persistedAuthSession, defaultState } = getPersistedAuth();

  if (persistedAuthSession) {
    return parseAuth$(persistedAuthSession, defaultState);
  }

  if (persistedAuth) {
    return parseAuth$(persistedAuth, defaultState);
  }

  return Promise.resolve(defaultState);
};

function startTokenRefresher<S extends { auth: IAuthState }>(
  api: ListenerEffectAPI<S, Dispatch>,
  onSuccess: (authState: IAuthState) => void,
  onError: (e: Error, snackbarMessage?: string) => void
) {
  stopTokenRefresher();

  const scheduleNextCheck = () => {
    setTokenRefresherTimer(checkToken, TOKEN_CHECK_INTERVAL_MS);
  };

  const checkToken = async () => {
    const { auth } = api.getState();
    if (!auth?.tokens.refresh) {
      scheduleNextCheck();
      return;
    }

    try {
      const isExpiringSoon = isTokenExpiringSoon(auth.tokens.access);

      if (isExpiringSoon) {
        const updatedState = await refreshToken(auth);

        if (!isTokenRefresherActive()) {
          return;
        }

        onSuccess(updatedState);
      }
    } catch (err) {
      if (isTokenRefresherActive()) {
        onError(err as Error, 'Token refresher error');
      }
      return;
    }

    if (isTokenRefresherActive()) {
      scheduleNextCheck();
    }
  };

  scheduleNextCheck();
}
const handleSuccess = <S>(authState: IAuthState, api: ListenerEffectAPI<S, Dispatch>) => {
  localStorage.setItem('auth', JSON.stringify(authState));
  setAuthorization(authState.tokens.access);
  api.dispatch(rehydrate(authState));
  api.dispatch(updateServicesAuthorizationStatus(true));
  setSentryUser(authState.user.id.toString(), authState.user.name, authState.user.email);
};

const handleError = <S>(e: Error, api: ListenerEffectAPI<S, Dispatch>, snackbarMessage?: string) => {
  stopTokenRefresher();
  unsetSentryUser();
  snackbarService.error(snackbarMessage ?? e.message);
  console.error('auth.rehydrate$', e);
  api.dispatch(rehydrate(initialState));
};

export const initListeners = () => {
  startAppListening({
    type: systemRehydrate.type,
    effect: async (_action, api) => {
      const onSuccess = (authState: IAuthState) => handleSuccess(authState, api);
      const onError = (e: Error, snackbarMessage?: string) => handleError(e, api, snackbarMessage);

      const state = api.getState();
      const user = selectUser(state);
      const isAdminState = isAdmin(state);
      if (!!user && !isAdminState) {
        const perms = await permsService.getPermsByCompany();
        api.dispatch(getPermsByCompany(perms.serialize()));
      }
      fetchIniAuthState()
        .then((authState) => {
          handleSuccess(authState, api);
          startTokenRefresher(api, onSuccess, onError);
        })
        .catch((e: Error) => {
          onError(e, `Login: ${e.message}`);
        });
    },
  });
};
